/**
 * Pure core of the offline sync: `pushLocalDraftToServer` replicates the exact
 * server save sequence from InspectionReportWorkflow, with the HTTP client
 * injected. Kept free of apiClient (MSAL/browser) imports so it runs in the node
 * vitest harness. The runtime engine (inspectionSync.ts) wires the real client.
 */
import type { LocalDraft } from './localDraftStore';
import { stripUnresolvedMarkers } from './photoQueue';
import { prunePhotoGeo, type PhotoGeoMap } from './photoLocation';

export interface ApiLike {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  put<T>(path: string, body: unknown): Promise<T>;
  del(path: string): Promise<unknown>;
}

// The slice of a server report needed to decide whether a local draft is already
// represented (and therefore safely discardable) on the server.
export interface ServerReportLite {
  id: string;
  job_site_id: string | null;
  month: string;
  is_draft: boolean | null;
}

/**
 * Pure: is this local draft already on the server as a SUBMITTED report (so it's
 * redundant and safe to drop without losing work)? Matches by the linked server
 * id when present, else by the property+month identity of a monthly inspection.
 * Only a non-draft (submitted/approved) server report counts — a User can't edit
 * one anyway, so any lingering local copy is moot; a server *draft* is left alone
 * because the local copy may hold newer edits still worth pushing.
 */
export function findServerMatch(
  draft: { serverReportId: string | null; jobSiteId: string; month: string },
  reports: ServerReportLite[],
): ServerReportLite | null {
  return (
    reports.find(
      (r) =>
        r.is_draft === false &&
        ((draft.serverReportId != null && r.id === draft.serverReportId) ||
          (r.job_site_id === draft.jobSiteId && r.month === draft.month)),
    ) ?? null
  );
}

/**
 * Push one local draft to the server, mirroring saveReport's exact sequence:
 * keep/create the report as a draft, rewrite all children, optionally flip to
 * submitted last (the User permission rule needs children written under a
 * draft). Strips any residual local-photo:// marker as a backstop — markers must
 * never reach the server. Returns the server report id + month.
 */
export async function pushLocalDraftToServer(
  draft: LocalDraft,
  opts: { api: ApiLike; isDraft: boolean },
): Promise<{ serverReportId: string; month: string }> {
  const { api, isDraft } = opts;
  const ref = draft.refData;
  if (!ref) throw new Error('local draft missing refData; cannot sync');

  const createReport = () =>
    api.post<{ id: string; month: string }>('/api/reports', {
      job_site_id: draft.jobSiteId,
      job_site_name: draft.jobSiteName,
      month: draft.month,
      is_draft: true,
      status_id: ref.draftStatusId,
      aspire_work_ticket_id: draft.aspireWorkTicketId ?? null,
      aspire_work_ticket_number: draft.aspireWorkTicketNumber ?? null,
    });

  let report: { id: string; month: string };
  let exists = true;
  const existingId = draft.serverReportId;

  if (existingId) {
    try {
      report = await api.put<{ id: string; month: string }>(`/api/reports/${existingId}`, {
        is_draft: true,
        status_id: ref.draftStatusId,
      });
    } catch (err) {
      // 404 = the linked server report is gone: deleted server-side, or a phantom
      // id that was never persisted. PUT-ing it forever is the stuck "save keeps
      // failing" loop. Detach and re-create instead of looping; the caller then
      // re-links the local draft to the fresh id (markSynced), preserving the work.
      if ((err as { status?: number } | undefined)?.status !== 404) throw err;
      report = await createReport();
      exists = false;
    }
  } else {
    report = await createReport();
    exists = false;
  }

  // One transactional request replaces the whole child set (see writeChildren).
  // `exists` only matters to the legacy fallback, which has to clear the old
  // rows itself.
  await writeChildren(api, report.id, draft, exists);

  if (!isDraft) {
    await api.put(`/api/reports/${report.id}`, {
      is_draft: false,
      status_id: ref.submittedStatusId,
      rejection_reason: '',
    });
  }

  return { serverReportId: report.id, month: report.month };
}

/**
 * Build the replace-all payload for PUT /api/reports/:id/children from the local
 * draft, resolving each row's snapshot fields (controller number/name/location,
 * zone number/type, double-check description/size) out of refData exactly as the
 * per-row POSTs used to. Zones whose master records are no longer in refData are
 * skipped, same as before. Markers are stripped as a backstop — a local-photo://
 * URL must never reach the server.
 */
function buildChildrenPayload(draft: LocalDraft) {
  const ref = draft.refData!;
  const clean = stripUnresolvedMarkers;
  // Keep photo_geo in step with the CLEANED url list. An unresolved marker is
  // stripped above, so sending its coordinates would leave the server holding a
  // key for a photo it never received — harmless, but it would accumulate, and
  // the entry comes back when that photo finally uploads under its real URL.
  const geoFor = (urls: string[], geo: PhotoGeoMap | undefined): PhotoGeoMap =>
    geo ? prunePhotoGeo(geo, clean(urls)) : {};

  const controller_inspections = draft.controllerInspections.map((c) => {
    const ctrl = ref.controllers.find((x) => x.id === c.controller_id);
    return {
      controller_id: c.controller_id,
      controller_number: ctrl?.controller_number,
      controller_description: ctrl?.name ?? '',
      controller_location: ctrl?.location ?? '',
      failure_codes: c.failure_codes,
      notes: c.notes,
      photo_urls: clean(c.photo_urls),
      photo_geo: geoFor(c.photo_urls, c.photo_geo),
    };
  });

  const doublecheck_inspections = draft.doublecheckInspections.map((d) => {
    const dc = ref.doubleChecks.find((x) => x.id === d.doublecheck_id);
    return {
      doublecheck_id: d.doublecheck_id,
      doublecheck_description: dc?.description ?? '',
      doublecheck_size: dc?.size != null ? String(dc.size) : null,
      failure_codes: d.failure_codes,
      notes: d.notes,
      photo_urls: clean(d.photo_urls),
      photo_geo: geoFor(d.photo_urls, d.photo_geo),
    };
  });

  const zone_inspections = [];
  for (const z of draft.zoneInspections) {
    const ctrl = ref.controllers.find((c) => c.zones.some((zz) => zz.id === z.zone_id));
    const zone = ctrl?.zones.find((zz) => zz.id === z.zone_id);
    if (!ctrl || !zone) continue;
    zone_inspections.push({
      zone_id: zone.id,
      controller_id: ctrl.id,
      controller_number: ctrl.controller_number,
      zone_number: zone.zone_number,
      type: zone.type,
      zone_type_name: zone.type,
      issues: z.issues.map((issue) => ({
        work_code: issue.work_code,
        notes: issue.notes,
        photo_urls: clean(issue.photo_urls),
        photo_geo: geoFor(issue.photo_urls, issue.photo_geo),
      })),
    });
  }

  return { controller_inspections, doublecheck_inspections, zone_inspections };
}

/**
 * Write the report's children. One transactional PUT replaces the entire set —
 * previously this was a per-row fan-out (GET all three child lists, DELETE every
 * row, POST every row back, with the zone POSTs serialized because each zone's
 * issues needed its new id). A 31-zone report cost ~75 round trips per autosave,
 * which on cellular ran 10s-2min and routinely stalled out as "Load failed",
 * tripping the autosave circuit breaker (feedback e42b0834).
 *
 * Falls back to the old fan-out only when the endpoint itself isn't there — i.e.
 * a 404 that is NOT our route's `not_found` body — which can happen for the few
 * seconds during a deploy when a fresh bundle is served by the previous server
 * process. Every other failure propagates: a permission or validation error must
 * surface, not be retried the slow way.
 */
async function writeChildren(
  api: ApiLike,
  reportId: string,
  draft: LocalDraft,
  reportExisted: boolean,
): Promise<void> {
  const payload = buildChildrenPayload(draft);
  // Guard against silent data loss: buildChildrenPayload joins each local zone
  // against refData (the master controllers/zones) and *skips* any zone it can't
  // resolve. If refData is missing/stale, every zone is dropped and we'd PUT an
  // empty zone set — which the server's replace-all turns into "delete all 145
  // zones, insert none" and reports success (feedback 385f24dd, Glen View). If we
  // hold zones locally but resolved none, refData isn't hydrated: abort loudly so
  // the work is preserved and retried once it hydrates, instead of wiping it. (The
  // server has a matching guard; this stops the destructive PUT from being sent.)
  if (draft.zoneInspections.length > 0 && payload.zone_inspections.length === 0) {
    throw new Error(
      `refData not hydrated: ${draft.zoneInspections.length} local zone(s) resolved to 0 — refusing to save an empty zone set that would wipe the report`,
    );
  }
  try {
    await api.put(`/api/reports/${reportId}/children`, payload);
  } catch (err) {
    const e = err as { status?: number; code?: string } | undefined;
    if (e?.status !== 404 || e?.code === 'not_found') throw err;
    await legacyWriteChildren(api, reportId, draft, reportExisted);
  }
}

/**
 * Pre-v1.5.36 per-row rewrite, kept only as the deploy-window fallback above.
 */
async function legacyWriteChildren(
  api: ApiLike,
  reportId: string,
  draft: LocalDraft,
  reportExisted: boolean,
): Promise<void> {
  const payload = buildChildrenPayload(draft);

  // DELETE a child row, tolerating a 404. The rewrite clears the server's copies
  // then re-posts from local state. If two saves overlap — e.g. a slow save (or
  // one that timed out with "Load failed") is still in flight when the next
  // fires — both read the same child list and both try to delete the same rows;
  // the loser's DELETE hits an already-gone row and 404s. That row is already in
  // the state we want, so a 404 here is success. Treating it as fatal used to
  // fail the whole save, and a few such collisions in a row tripped the
  // "autosave stopped after repeated failures" circuit breaker (feedback
  // 93b7af83) even though no work was lost.
  const delIgnoreMissing = async (path: string) => {
    try {
      await api.del(path);
    } catch (err) {
      if ((err as { status?: number } | undefined)?.status !== 404) throw err;
    }
  };

  if (reportExisted) {
    const [ci, di, zi] = await Promise.all([
      api.get<{ id: string }[]>(`/api/controller-inspections?reportId=${reportId}`),
      api.get<{ id: string }[]>(`/api/doublecheck-inspections?reportId=${reportId}`),
      api.get<{ id: string }[]>(`/api/zone-inspections?reportId=${reportId}`),
    ]);
    await Promise.all([
      ...ci.map((r) => delIgnoreMissing(`/api/controller-inspections/${r.id}`)),
      ...di.map((r) => delIgnoreMissing(`/api/doublecheck-inspections/${r.id}`)),
      ...zi.map((r) => delIgnoreMissing(`/api/zone-inspections/${r.id}`)),
    ]);
  }

  await Promise.all(
    payload.controller_inspections.map((c) =>
      api.post('/api/controller-inspections', { report_id: reportId, ...c }),
    ),
  );
  await Promise.all(
    payload.doublecheck_inspections.map((d) =>
      api.post('/api/doublecheck-inspections', { report_id: reportId, ...d }),
    ),
  );
  for (const { issues, ...zone } of payload.zone_inspections) {
    const ziCreated = await api.post<{ id: string }>('/api/zone-inspections', {
      report_id: reportId,
      ...zone,
    });
    if (issues.length > 0) {
      await Promise.all(
        issues.map((issue) =>
          api.post('/api/zone-issues', { zone_inspection_id: ziCreated.id, ...issue }),
        ),
      );
    }
  }
}
